// THE FILE BRIEFING — the box while a stored document is open (Figma "Task creation design",
// desktop frames 60–63 "open file 1–4" and mobile m61–m64 "open file 17–20"; Pouria, 2026-10-15:
// "make sure we implement exactly things like this").
//
// Opening a file no longer throws a viewer over the middle. It puts the BOX into a state about that
// file — "Briefing <file>" with a Select tick, Details / Summary tabs, a facts grid, the first page
// as a thumbnail and "Possible follow-ups" — and the middle becomes the file's conversation (S5's
// flow G). "Open" on the thumbnail is what launches the full-window viewer (frame 64,
// components/SmartBox/FileViewer.vue).
//
// This module is every rule of that state that is not drawing: what counts as a file briefing, the
// words, the facts grid and what each cell is read from, the follow-ups, the viewer's header lines
// and page pill, the Work-board chip a Select makes and the rail's selection line. No store, no
// fetch, no component — the record is busy-main-bff's DocumentResponse as the Recent files page and
// the attachment lists already read it.
//
// ── WHAT THE RECORD CAN AND CANNOT SAY (DocumentResponse, 2026-10-15) ───────────────────────────
//   • Kind           — `contentType` / `filename` (lib/recentFilesPage `fileTypeOf`), `octets`, and
//                      the page count the renderer reports once the PDF is decoded (`pages`).
//   • Ongoing        — a document carries no Ongoing name. Inside a story the open story's title is
//                      used; elsewhere the row is dropped (flagged).
//   • Received       — `createdOn` (the upload, or the mail sync).
//   • Last opened    — no "opened" timestamp exists. The moment THIS open happened is written, which
//                      is the closest true thing the client knows (flagged).
//   • From           — `relatedEmail` (who the conversation the file came in on is with).
//   • Related email  — `timelineHeadLine` · the provider's name (lib/recentFilesPage `originOf`).
//   • Saved location — read off `provider`: a Drive row is "Busy files · Google Drive", a mail part
//                      is "Gmail", a link is "Link" (flagged — a copy made by "Save to Files" is a
//                      Drive row too, so the frame's exact wording holds for it).

import { fileExtension } from '@/lib/briefings/entityFiles'
import { formatBytes } from '@/lib/briefings/image'
import { previewKindOf } from '@/lib/attachmentOpen'
import { fileTypeOf, originOf } from '@/lib/recentFilesPage'
import { parseUtc } from '@/lib/dueStatus'
import { driveIdOf } from '@/lib/recentSavedFiles'
import { mediaKindOf } from '@/lib/mediaPlayer'

/** The state's own words — frames 60–64 and m60–m64, verbatim. */
export const FILE_TEXT = {
  briefing: 'Briefing',
  select: 'Select',
  selected: 'Selected',
  collapse: 'Collapse',
  expand: 'Expand',
  details: 'Details',
  summary: 'Summary',
  // The Summary tab (frame 61) and its waiting / empty / failed lines.
  overview: 'Overview',
  keyInformation: 'Key information',
  summaryLoading: 'Writing a summary of this file…',
  summaryNone: 'There is no text in this file to summarise.',
  summaryError: 'The summary could not be written.',
  summaryRetry: 'Try again',
  followUps: 'Possible follow-ups',
  moreDetails: 'More details',
  open: 'Open',
  download: 'Download',
  delete: 'Delete',
  close: 'Close',
  previous: 'Previous file',
  next: 'Next file',
  page: 'Page',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  placeholder: 'Ask about this document or search anything....',
  // The phone's ask area writes the same words with a true ellipsis (busy-my-web#636, Figma
  // 40014298:306603 "open file 27") — m61's short "Ask or search anything..." is gone.
  placeholderMobile: 'Ask about this document or search anything\u2026',
  send: 'Ask',
  // The conversation's running state (frames 71 / m71, stream S5): the stop's label. The running
  // line itself names the file's kind on a phone too now (lib/fileConversation.js, #636).
  stop: 'Stop answering',
  board: 'Work-board',
  boardEmpty: 'No files yet',
  boardSelected: 'Selected items',
  confirmDelete: 'Delete this file? It is removed from Busy for good.',
  confirmYes: 'Delete',
  confirmNo: 'Cancel',
  opened: 'File opened',
}

/** The two tabs, in the frame's order. */
export const FILE_TABS = ['details', 'summary']

/**
 * Is the item open in the preview a STORED DOCUMENT — one the box briefs rather than the middle
 * viewer draws? A research photo, a link or a snippet carries no `documentId`; a file opened from
 * the Recent files page, a Work-board chip or an entity's attachments does.
 */
export function isFileBriefing(item) {
  const it = item?.item || item
  return !!(it && it.documentId)
}

/** The document's file name, whichever field carries it. */
export function fileName(doc) {
  return String(doc?.filename || doc?.fileName || doc?.name || doc?.title || '').trim()
}

/** The glyph and colour a file wears — the Recent files page's own per-type palette. */
export function fileGlyph(doc) {
  const type = fileTypeOf({ filename: fileName(doc), contentType: doc?.contentType })
  // The red PDF page is the frames' one typed glyph (60–64, m60–m64); everything else keeps the
  // page's generic file silhouette in the type's colour.
  return { name: type.key === 'pdf' ? 'folder-pdf' : 'file', color: type.color, label: type.label, key: type.key }
}

/** Is the document a picture (drawn as an image rather than as pages)? */
export function isImageDocument(doc) {
  return fileGlyph(doc).key === 'image'
}

/** Is the document a PDF (rendered page by page)? */
export function isPdfDocument(doc) {
  return fileGlyph(doc).key === 'pdf'
}

/**
 * Is the document a VIDEO — played in the viewer's 16:9 card (busy-my-web#639, Figma "video view"
 * 40014356:382673)? mp4 / webm / mov, by content type first and extension second
 * (lib/mediaPlayer `mediaKindOf`).
 */
export function isVideoDocument(doc) {
  return mediaKindOf(doc?.contentType, fileName(doc)) === 'video'
}

/**
 * Is the document AUDIO — played in the viewer's audio card (busy-my-web#639, Figma "Audio view 1"
 * 40014356:382752)? mp3 / m4a / wav / ogg / aac, by content type first and extension second.
 */
export function isAudioDocument(doc) {
  return mediaKindOf(doc?.contentType, fileName(doc)) === 'audio'
}

// ── Dates ────────────────────────────────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function toDate(stamp) {
  if (stamp === null || stamp === undefined || stamp === '') return null
  const d = stamp instanceof Date ? stamp : typeof stamp === 'number' ? new Date(stamp) : parseUtc(stamp)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "12:00 PM" — the frame's clock. */
function clock(d) {
  const h = d.getHours()
  const m = String(d.getMinutes()).padStart(2, '0')
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${m} ${h < 12 ? 'AM' : 'PM'}`
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** "12 Oct 2026, 12:00 PM" (frame 60's "Received"). '' for a missing or unreadable stamp. */
export function fileDateTime(stamp) {
  const d = toDate(stamp)
  if (!d) return ''
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${clock(d)}`
}

/** "Today, 11:06 AM" / "Yesterday, 6:40 PM" / "12 Oct 2026, 12:00 PM" (frame 60's "Last opened"). */
export function openedText(stamp, now = Date.now()) {
  const d = toDate(stamp)
  if (!d) return ''
  const today = new Date(now)
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  if (sameDay(d, today)) return `Today, ${clock(d)}`
  if (sameDay(d, yesterday)) return `Yesterday, ${clock(d)}`
  return fileDateTime(d)
}

/** "Oct 2" — the short date after the viewer's file name (frame 64). */
export function shortDate(stamp) {
  const d = toDate(stamp)
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}` : ''
}

/** "2 Sep 2026" — the date closing the viewer's provenance line (frame 64). */
export function longDate(stamp) {
  const d = toDate(stamp)
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : ''
}

// ── Facts ────────────────────────────────────────────────────────────────────────────────────────

/** The document's byte size, whichever field carries it. */
export function fileBytes(doc) {
  return doc?.octets ?? doc?.size ?? doc?.bytes ?? null
}

/** "2 pages" / "1 page" / '' — the page count once the renderer knows it. */
export function pagesText(pages) {
  const n = Number(pages) || 0
  return n > 0 ? `${n} page${n === 1 ? '' : 's'}` : ''
}

/** "PDF · 240 KB · 2 pages" (frame 60's "Kind"). */
export function kindLine(doc, pages = null) {
  return [fileGlyph(doc).label, formatBytes(fileBytes(doc)), pagesText(pages)].filter(Boolean).join(' · ')
}

/** "PDF · 240 KB" (m61's "Type" — no page count on the phone card). */
export function typeLine(doc) {
  return kindLine(doc, null)
}

/** "Your Prada order · Gmail" — the thread's subject and where it lives. */
export function relatedEmailLine(doc) {
  return [String(doc?.timelineHeadLine || '').trim(), originOf(doc)].filter(Boolean).join(' · ')
}

/** "Busy files · Google Drive" / "Gmail" / "Link" — see the note at the top. */
export function savedLocation(doc) {
  const origin = originOf(doc)
  if (origin === 'Google drive') return 'Busy files · Google Drive'
  return origin
}

/**
 * The facts grid (frame 60): two columns of label / value pairs, empties dropped.
 *
 * @param {object} doc      the DocumentResponse
 * @param {{pages?:number, ongoing?:string, openedAt?:number|string|Date, now?:number}} ctx
 * @returns {{ left: {key:string,label:string,value:string}[], right: {key:string,label:string,value:string}[] }}
 */
export function fileFacts(doc, { pages = null, ongoing = '', openedAt = null, now = Date.now() } = {}) {
  const cell = (key, label, value) => ({ key, label, value: String(value || '').trim() })
  const keep = (cells) => cells.filter(c => c.value)
  return {
    left: keep([
      cell('kind', 'Kind:', kindLine(doc, pages)),
      cell('ongoing', 'Ongoing:', ongoing),
      cell('received', 'Received:', fileDateTime(doc?.createdOn || doc?.createdDate || doc?.createdAt)),
      cell('opened', 'Last opened:', openedText(openedAt, now)),
    ]),
    right: keep([
      cell('from', 'From:', doc?.relatedEmail),
      cell('email', 'Related email:', relatedEmailLine(doc)),
      cell('saved', 'Saved location:', savedLocation(doc)),
    ]),
  }
}

/**
 * The phone card's detail lines (busy-my-web#636, Figma 40014298:306603 "open file 27"), each a
 * "Label: value" line — File name, Kind (its first word dark, the rest grey: "PDF · 240 KB · 2 pages"),
 * Ongoing (a link to the story) and Received — superseding m61's Type / Attached to / Ongoing rows.
 * "More details" opens the rest of the desktop grid under them (see `moreDetailLines`).
 *
 * @returns {{ key: string, label: string, value: string, rest: string, link: object|null }[]}
 */
export function mobileDetailLines(doc, { pages = null, ongoing = '', ongoingId = '' } = {}) {
  const [kindHead, ...kindRest] = kindLine(doc, pages).split(' · ')
  const lines = [
    { key: 'name', label: 'File name:', value: fileName(doc), rest: '', link: null },
    { key: 'kind', label: 'Kind:', value: kindHead || '', rest: kindRest.length ? ` · ${kindRest.join(' · ')}` : '', link: null },
    { key: 'ongoing', label: 'Ongoing:', value: String(ongoing || '').trim(), rest: '', link: ongoingId ? { ongoingId: String(ongoingId) } : null },
    { key: 'received', label: 'Received:', value: fileDateTime(doc?.createdOn || doc?.createdDate || doc?.createdAt), rest: '', link: null },
  ]
  return lines.filter(l => l.value)
}

/** What the phone's "More details" adds: the desktop grid less what the lines above already say. */
export function moreDetailLines(facts) {
  const shown = new Set(['kind', 'ongoing', 'received'])
  return [...(facts?.left || []), ...(facts?.right || [])].filter(f => f && !shown.has(f.key))
}

// ── Follow-ups ───────────────────────────────────────────────────────────────────────────────────

/**
 * The two "Possible follow-ups" (frame 60), from what the record says about the file. No model is
 * asked: a receipt or an invoice is asked for its total, any other file what it is about, and the
 * second question looks for the rest of the conversation the file came in on. Clicking one asks it
 * (the store's `ask`).
 */
export function followUps(doc) {
  // (The phone's shorter pair — m61's "What is the total?" / "Find the emails about …" — is gone: the
  // updated frames ask the desktop's own questions inside the card, busy-my-web#636.)
  const cls = String(doc?.class || '').toLowerCase()
  const money = cls === 'receipt' || cls === 'invoice'
  const first = money ? 'What is the total amount paid?' : 'What is this document about?'
  const subject = String(doc?.timelineHeadLine || '').trim()
  const who = String(doc?.relatedEmail || '').trim()
  const second = subject
    ? `Find other emails about ${subject}`
    : who
      ? `Find other emails from ${who}`
      : 'Which Ongoing does this belong to?'
  return [first, second]
}

// ── The viewer (frame 64 / m60) ──────────────────────────────────────────────────────────────────

/**
 * The viewer's top-left: the file name with its short date, and under it the provenance line
 * "<Ongoing> → <source> · <provider> . <date>", each part only when known.
 */
export function viewerHeader(doc, { ongoing = '' } = {}) {
  const when = doc?.createdOn || doc?.createdDate || doc?.createdAt || null
  const source = relatedEmailLine(doc)
  const trail = [String(ongoing || '').trim(), source].filter(Boolean).join(' → ')
  const date = longDate(when)
  return {
    name: fileName(doc),
    date: shortDate(when),
    line: [trail, date].filter(Boolean).join(' . '),
  }
}

/** The zoom steps the pill's − / + walk, 100% in the middle (frame 64). */
export const ZOOM_MIN = 0.5
export const ZOOM_MAX = 3
export const ZOOM_STEP = 0.25

/** The next zoom in direction `d` (±1), clamped. */
export function nextZoom(zoom, d) {
  const z = Math.round((Number(zoom) || 1) * 100) / 100
  const next = Math.round((z + (d > 0 ? ZOOM_STEP : -ZOOM_STEP)) * 100) / 100
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next))
}

/** "100%" */
export function zoomText(zoom) {
  return `${Math.round((Number(zoom) || 1) * 100)}%`
}

/** "1 / 2" — the page badge (frame 60) and the pill's count (frame 64). */
export function pageText(page, pages) {
  const n = Number(pages) || 0
  if (n <= 0) return ''
  const p = Math.min(Math.max(Number(page) || 1, 1), n)
  return `${p} / ${n}`
}

/**
 * Which page is "current" while the pages scroll (frame 64's pill): the one covering the middle of
 * the viewport, from the pages' top offsets. 1-based; 1 when nothing is known.
 *
 * @param {number[]} tops   each page's top, relative to the scroller's content
 * @param {number} scrollTop
 * @param {number} viewHeight
 */
export function currentPage(tops, scrollTop, viewHeight) {
  const list = Array.isArray(tops) ? tops : []
  if (!list.length) return 1
  const middle = (Number(scrollTop) || 0) + (Number(viewHeight) || 0) / 2
  let page = 1
  list.forEach((top, i) => { if (Number(top) <= middle) page = i + 1 })
  return page
}

/**
 * The files the viewer can DRAW out of a list — a picture, a PDF, a text file, a video or an audio
 * file (lib/attachmentOpen's rule; media since #639). The arrows step through these alone: a spreadsheet or a .docx in the list would otherwise
 * be downloaded by a step, which is not what an arrow promises.
 */
export function viewableDocuments(list) {
  return (Array.isArray(list) ? list : []).filter(r => r && r.id != null && previewKindOf(openableDocument(r)) !== null)
}

/** The sibling `d` (±1) steps to in `list`, by id — wrapping; null with fewer than two files. */
export function siblingOf(list, id, d) {
  const rows = (Array.isArray(list) ? list : []).filter(r => r && r.id != null)
  if (rows.length < 2) return null
  const at = rows.findIndex(r => String(r.id) === String(id))
  if (at < 0) return rows[0]
  return rows[(at + (d > 0 ? 1 : -1) + rows.length) % rows.length]
}

// ── Select → the Work-board and the rail (frame 63) ──────────────────────────────────────────────

/** The item a Select puts on the Work-board: the stored document, as a `file` kind. */
export function fileChip(doc) {
  const id = String(doc?.id || '')
  if (!id) return null
  const name = fileName(doc)
  return {
    id,
    kind: 'file',
    title: name,
    contentType: String(doc?.contentType || ''),
    documentId: id,
    // The Drive id Create links by (busy-my-web#645): a Selected file on a creation board becomes a
    // tick-box row there (lib/boardFiles.js), and an attached row with no Drive id cannot be linked.
    fileId: driveIdOf(doc),
    size: fileBytes(doc),
  }
}

/** "1 file selected" — the rail's line under "Ready when you are" (frame 63). */
export function fileSelectionLine(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  return `${n} file${n === 1 ? '' : 's'} selected`
}

/** The name split for the rail row: "Receipt" + ".jpeg" as a span of its own. */
export function railRow(doc) {
  const name = fileName(doc)
  const ext = fileExtension(name)
  return {
    id: String(doc?.id || ''),
    name,
    base: ext ? name.slice(0, name.length - ext.length - 1) : name,
    ext,
    glyph: fileGlyph(doc),
  }
}

/** A document as lib/attachmentOpen's stored-document helpers want it. */
export function openableDocument(doc) {
  return {
    id: String(doc?.id || ''),
    name: fileName(doc) || 'Attachment',
    contentType: String(doc?.contentType || ''),
    isImage: isImageDocument(doc),
  }
}

export default fileFacts
